import type { FC, ReactNode } from 'react';
import { useState, useEffect, useCallback, createContext } from 'react';
import { useUserPreference } from '@console/shared/src/hooks/useUserPreference';
import type { DisplayFilters } from '../topology-types';
import { DEFAULT_TOPOLOGY_FILTERS } from './const';

const TOPOLOGY_DISPLAY_FILTERS_USER_PREFERENCE_KEY = `devconsole.topology.filters`;

const getTopologyFilters = (appliedFilters: AppliedFilters) => {
  const filters = [...DEFAULT_TOPOLOGY_FILTERS];
  filters.forEach((filter) => {
    if (appliedFilters[filter.id] !== undefined) {
      filter.value = appliedFilters[filter.id];
    }
  });

  return filters;
};

const getAppliedFilters = (filters: DisplayFilters): { [id: string]: boolean } => {
  if (!filters?.length) {
    return {};
  }

  return filters.reduce((acc, filter) => {
    acc[filter.id] = filter.value;
    return acc;
  }, {});
};

type AppliedFilters = { [filterKey: string]: boolean };
type SetTopologyFilters = (filters: DisplayFilters) => void;

const useFilterContextValues = (): [
  DisplayFilters,
  AppliedFilters,
  boolean,
  SetTopologyFilters,
] => {
  const [appliedFilters, setAppliedFilters, appliedFiltersLoaded] = useUserPreference(
    TOPOLOGY_DISPLAY_FILTERS_USER_PREFERENCE_KEY,
    getAppliedFilters(DEFAULT_TOPOLOGY_FILTERS),
  );
  const [filtersLoaded, setFiltersLoaded] = useState<boolean>(false);
  const [filters, setFilters] = useState<DisplayFilters>([]);

  useEffect(() => {
    if (appliedFiltersLoaded && !filtersLoaded) {
      setFilters(getTopologyFilters(appliedFilters));
      setFiltersLoaded(true);
    }
  }, [appliedFilters, appliedFiltersLoaded, filtersLoaded]);

  const setTopologyFilters = useCallback(
    (displayFilters: DisplayFilters) => {
      setFilters(displayFilters);
      setAppliedFilters(getAppliedFilters(displayFilters));
    },
    [setAppliedFilters],
  );

  return [filters, appliedFilters, appliedFilters && filtersLoaded, setTopologyFilters];
};

type FilterContextType = {
  filters?: DisplayFilters;
  appliedFilters?: AppliedFilters;
  setTopologyFilters?: SetTopologyFilters;
};

export const FilterContext = createContext<FilterContextType>({});

interface FilterProviderProps {
  children?: ReactNode;
}

export const FilterProvider: FC<FilterProviderProps> = ({ children }) => {
  const [filters, appliedFilters, loaded, setTopologyFilters] = useFilterContextValues();
  return (
    <FilterContext.Provider value={{ filters, appliedFilters, setTopologyFilters }}>
      {loaded ? children : null}
    </FilterContext.Provider>
  );
};
